import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * ArchivePlanDialog — generic confirmation Dialog shown when an operator flips a plan
 * from Active → Archived on the /spaces inline plan expansion (Plan 25-05).
 *
 * Copy is intentionally generic per Phase 25 CONTEXT + RESEARCH §Open-Question-1:
 * Phase 25 does NOT add a new server endpoint to count currently-active memberships
 * for a given plan. Operators can sanity-check via the Accounts list / audit log if
 * needed. Phase 26 may revisit and upgrade the copy with a live N-active count.
 *
 * Behavior contract:
 * - `open` / `onOpenChange` are fully controlled by the parent.
 * - `confirming` disables both buttons while the parent's PATCH mutation is in flight.
 * - The Confirm button calls `e.preventDefault()` before invoking `onConfirm` so the
 *   AlertDialog does NOT auto-close before the mutation settles. The parent is
 *   responsible for closing the dialog (`onOpenChange(false)`) on success.
 */
export interface ArchivePlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  planName: string;
  onConfirm: () => Promise<void> | void;
  confirming: boolean;
}

export function ArchivePlanDialog({
  open,
  onOpenChange,
  planName,
  onConfirm,
  confirming,
}: ArchivePlanDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Archive plan{planName ? ` "${planName}"` : ""}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            This plan may have active memberships using it. Archiving doesn&apos;t break
            existing memberships but new bookings will fail until the plan is
            reactivated. Continue?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={confirming}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              void onConfirm();
            }}
            disabled={confirming}
          >
            {confirming ? "Archiving…" : "Archive Plan"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
